import { Button, ButtonProps } from '@/components/ui/button';
import { FlexCol, FlexRow, type OrientedFlexProps } from '@/components/ui/flex';
import { Text, type TextProps } from '@/components/ui/text';
import { HandleSubmitMessageType } from '@/utils/aiUtils';
import { cn } from '@/utils/classUtils';
import { DtUIMessage, GetChatByIdResponse } from '@ditto/shared';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { ArrowDown, Stars, X } from 'lucide-react';
import { useEffect, type FC, type MouseEventHandler, type Ref } from 'react';
import { AiReasoningBlock } from '../../AiReasoningBlock';
import { Capabilities } from './Capabilities';
import { ChatContextProvider, useChatCtx } from './ChatContext';
import { DocumentPreview } from './DocumentPreview';
import { Input } from './Input';
import { Message } from './Message';
import { MessageAttachmentComponent } from './MessageAttachment';
import {
  AiTextMessage,
  ClassificationBlock,
  ErrorMessage,
  UserMessage,
} from './Messages';
import { TableViewChatMessage } from './TableViewChatMessage';
import { Tool } from './Tool';

function useButtonScrollCtx() {
  const { isScrollButtonVisible, scrollRef } = useChatCtx();

  const scrollToBottom = () => {
    scrollRef?.current?.scrollToBottom();
  };

  return {
    isScrollButtonVisible,
    scrollToBottom,
  };
}

function useContentScrollCtx() {
  const { contentCbRef, onContentUnmount } = useChatCtx();

  return {
    contentCbRef,
    onContentUnmount,
  };
}

const ScrollToBottomButton: FC<ButtonProps> = ({ onClick, ...props }) => {
  const { isScrollButtonVisible, scrollToBottom } = useButtonScrollCtx();

  const handleClick: MouseEventHandler<HTMLButtonElement> = evt => {
    scrollToBottom();
    onClick?.(evt);
  };

  if (!isScrollButtonVisible) return null;

  return (
    <FlexRow justifyContent="center" maxH="0">
      <Button
        className="rounded-full -translate-y-7 self-center h-[32px] w-[32px]"
        variant="outline"
        size="sm"
        onClick={handleClick}
        {...props}
      >
        <ArrowDown />
      </Button>
    </FlexRow>
  );
};

const Layout: FC<OrientedFlexProps> = ({ className, ...props }) => {
  return (
    <FlexCol
      h="full"
      justifyContent="between"
      alignItems="center"
      className={cn('flex-1 min-w-0', className)}
      {...props}
    />
  );
};

const Content: FC<OrientedFlexProps> = ({ className, children, ...props }) => {
  const { contentCbRef, onContentUnmount } = useContentScrollCtx();

  useEffect(() => {
    return () => {
      onContentUnmount();
    };
  }, [onContentUnmount]);

  return (
    <FlexCol
      w="full"
      h="full"
      alignItems="center"
      className={cn('overflow-y-auto relative', className)}
      ref={contentCbRef}
    >
      <FlexCol
        gap="6"
        pt="2"
        h="full"
        className={cn(
          //This is to make sure that the user message is scrolled to the top when sent
          '[&>div:last-of-type]:min-h-[calc(100%-60px)]',
          //This is to avoid having the space between the input and the messages when scrolling, but to have some space at the end of the last message
          '[&>div:last-of-type_.AiMsgBottom]:!block',
          'w-full max-w-[950px] px-10 ',
          className
        )}
        {...props}
      >
        {children}
      </FlexCol>
    </FlexCol>
  );
};

const Header: FC<
  Omit<OrientedFlexProps, 'children'> & {
    onClose?: () => void;
  }
> = ({ className, onClose, ...props }) => {
  return (
    <FlexRow
      alignItems="center"
      justifyContent="between"
      p="3"
      w="full"
      className={cn(className, 'min-h-[65px] border-b border-neutral-300')}
      {...props}
    >
      <FlexRow gap="2">
        {!!onClose && (
          <Button variant="outline" size="icon" onClick={onClose}>
            <X />
          </Button>
        )}
      </FlexRow>
    </FlexRow>
  );
};

const Date: FC<Omit<TextProps, 'children'> & { date: Date }> = ({
  date,
  ...props
}) => {
  dayjs.extend(LocalizedFormat);
  const displayedDate = dayjs(date).format('MMM D, hh:mm A');
  return (
    <FlexRow justifyContent="center">
      <Text variant="body-sm" color="secondary" {...props}>
        {displayedDate}
      </Text>
    </FlexRow>
  );
};

const EmptyState: FC<
  OrientedFlexProps & {
    title: string;
    description?: string;
    imageUrl?: string;
    ref?: Ref<HTMLDivElement>;
  }
> = ({ title, description, imageUrl, ...props }) => {
  return (
    <FlexCol
      gap="4"
      px="10"
      h="full"
      w="full"
      alignItems="center"
      justifyContent="center"
      {...props}
    >
      {!!imageUrl && <img src={imageUrl} className=" w-18 h-18 rounded-full" />}
      <FlexCol gap="1.5" alignItems="center">
        <Text variant="header1" color="primary">
          {title}
        </Text>
        <FlexRow gap="2" alignItems="center" justifyContent="center">
          <Stars className="text-accent-400" />
          <Text variant="body-lg" color="primary">
            {description}
          </Text>
        </FlexRow>
      </FlexCol>
    </FlexCol>
  );
};

type ChatType = FC<
  OrientedFlexProps & {
    ref?: Ref<HTMLDivElement>;
    messages?: DtUIMessage[];
    sendMessage: (message: HandleSubmitMessageType) => void;
    chatData?: GetChatByIdResponse | null;
    showFollowUpQuestions?: boolean;
  }
> & {
  Layout: typeof Layout;
  Content: typeof Content;
  Date: typeof Date;
  AiTextMessage: typeof AiTextMessage;
  AiToolMessage: typeof Tool;
  MessageAttachment: typeof MessageAttachmentComponent;
  UserMessage: typeof UserMessage;
  ErrorMessage: typeof ErrorMessage;
  ClassificationBlock: typeof ClassificationBlock;
  Input: typeof Input;
  DocumentPreview: typeof DocumentPreview;
  EmptyState: typeof EmptyState;
  Header: typeof Header;
  ScrollToBottomButton: typeof ScrollToBottomButton;
  Capabilities: typeof Capabilities;
  Message: typeof Message;
  AiReasoningBlock: typeof AiReasoningBlock;
  TableViewChatMessage: typeof TableViewChatMessage;
};

export const Chat: ChatType = ({
  className,
  messages = [],
  sendMessage,
  chatData = null,
  showFollowUpQuestions = true,
  ...props
}) => {
  return (
    <ChatContextProvider
      messages={messages}
      sendMessage={sendMessage}
      chatData={chatData}
      showFollowUpQuestions={showFollowUpQuestions}
    >
      <FlexCol
        h="full"
        w="full"
        justifyContent="between"
        alignItems="center"
        className={cn('bg-white', className)}
        {...props}
      />
    </ChatContextProvider>
  );
};

Chat.Layout = Layout;
Chat.Content = Content;
Chat.Date = Date;
Chat.AiTextMessage = AiTextMessage;
Chat.AiToolMessage = Tool;
Chat.MessageAttachment = MessageAttachmentComponent;
Chat.UserMessage = UserMessage;
Chat.ErrorMessage = ErrorMessage;
Chat.ClassificationBlock = ClassificationBlock;
Chat.Input = Input;
Chat.DocumentPreview = DocumentPreview;
Chat.EmptyState = EmptyState;
Chat.Header = Header;
Chat.ScrollToBottomButton = ScrollToBottomButton;
Chat.Capabilities = Capabilities;
Chat.Message = Message;
Chat.AiReasoningBlock = AiReasoningBlock;
Chat.TableViewChatMessage = TableViewChatMessage;
